import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { LayoutAwareForm } from '@/components/crm/layout-aware-form'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/contacts/$contactId/edit')({
  component: EditContactPage,
})

function EditContactPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { contactId } = Route.useParams()

  const { data, isLoading } = useQuery(
    orpc.crm.contacts.get.queryOptions({ input: { id: contactId } }),
  )

  const updateMutation = useMutation(
    orpc.crm.contacts.update.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.common.save'))
        queryClient.invalidateQueries({ queryKey: orpc.crm.contacts.list.key() })
        queryClient.invalidateQueries({
          queryKey: orpc.crm.contacts.get.queryKey({ input: { id: contactId } }),
        })
        navigate({ to: '/crm/contacts/$contactId', params: { contactId } })
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  if (isLoading || !data) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: '/crm/contacts/$contactId', params: { contactId } })}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('crm.common.cancel')}
        </Button>
        <h1 className="text-2xl font-bold">
          {t('crm.common.edit')} {t('crm.contact.entity').toLowerCase()}
        </h1>
      </div>
      <LayoutAwareForm
        entityKey="contact"
        initial={data as Record<string, unknown>}
        onSubmit={(value) =>
          updateMutation.mutate({ id: contactId, ...value } as Parameters<
            typeof updateMutation.mutate
          >[0])
        }
        submitting={updateMutation.isPending}
        submitLabel={t('crm.common.save')}
        onCancel={() => navigate({ to: '/crm/contacts/$contactId', params: { contactId } })}
      />
    </div>
  )
}
